<template>
  <v-main>
    <v-container fluid>
      <!-- Header Section -->
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card elevation="3">
            <!-- Page Header -->
            <page-header
              title="Council Certificate Approval"
              subtitle="Review and approve pending council registration certificates"
            >
              <template v-slot:actions>
                <div class="d-flex justify-end align-center">
                    <v-chip
                      color="warning"
                      outlined
                      small
                      class="font-weight-medium"
                    >
                      <v-icon left x-small>mdi-clock-alert</v-icon>
                      {{ pendingCount }} Pending
                    </v-chip>
                  </div>
              </template>
            </page-header>

            <!-- Filtration Section -->
            <v-card-subtitle class="pb-1 pt-2 px-4">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20">mdi-filter-variant</v-icon>
                    <span class="text-body-2 font-weight-medium">Filter Council Certificates</span>
                  </div>
                </v-col>
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip
                      v-if="hasActiveFilters"
                      color="info"
                      x-small
                      class="mr-2"
                    >
                      <v-icon left x-small>mdi-filter</v-icon>
                      {{ getActiveFiltersCount() }} Active
                    </v-chip>
                    <v-btn
                      text
                      x-small
                      color="error"
                      @click="clearAllFilters"
                      :disabled="!hasActiveFilters"
                    >
                      <v-icon left x-small>mdi-filter-remove</v-icon>
                      Clear
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-subtitle>

            <v-card-text class="px-4 pb-4">
              <v-row>
                <!-- Search Field -->
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="filter.search_keyword"
                    label="Search Council Certificates"
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    dense
                    clearable
                    @input="handleSearch"
                  ></v-text-field>
                </v-col>

                <!-- Sort Options -->
                <v-col cols="12" md="4">
                  <v-select
                    v-model="filter.sort_by"
                    :items="sortOptions"
                    label="Sort by"
                    prepend-inner-icon="mdi-sort"
                    outlined
                    dense
                    @change="applyFilters"
                  ></v-select>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Data Table -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3">
            <v-data-table
              :headers="headers"
              :items="certificates"
              :loading="loading"
              :items-per-page="rowsPerPage"
              :page.sync="currentPage"
              :server-items-length="totalItems"
              hide-default-footer
              class="elevation-1"
              item-key="id"
            >
              <!-- Loading slot -->
              <template #loading>
                <div class="text-center pa-4">
                  <health-loader message="Loading council certificates..." />
                </div>
              </template>

              <!-- No data slot -->
              <template #no-data>
                <div class="text-center pa-8">
                  <v-icon color="grey lighten-1" size="60">mdi-certificate-outline</v-icon>
                  <div class="text-h6 grey--text mt-4">No pending council certificates found</div>
                  <div class="text-body-2 grey--text">All council certificates have been reviewed</div>
                </div>
              </template>

              <!-- User Info Column -->
              <template #[`item.user_info`]="{ item }">
                <div class="d-flex align-center py-2">
                  <v-avatar size="40" class="mr-3">
                    <v-icon>mdi-account</v-icon>
                  </v-avatar>
                  <div>
                    <div class="font-weight-medium text-subtitle-2">
                      {{ item.user ? (item.user.first_name + ' ' + item.user.last_name) : 'N/A' }}
                    </div>
                    <div class="text-caption grey--text" v-if="item.user">
                      {{ item.user.email }}
                    </div>
                  </div>
                </div>
              </template>

              <!-- Council Details Column -->
              <template #[`item.council_details`]="{ item }">
                <div>
                  <div class="font-weight-medium">{{ item.council_name }}</div>
                  <div class="text-caption grey--text">{{ item.registration_number }}</div>
                </div>
              </template>

              <!-- Registration Year Column -->
              <template #[`item.registration_year`]="{ item }">
                <div>
                  <div class="font-weight-medium">{{ item.registration_year }}</div>
                  <div class="text-caption grey--text" v-if="item.expiry_date">
                    Expires: {{ formatDate(item.expiry_date) }}
                  </div>
                </div>
              </template>

              <!-- Certificate Column -->
              <template #[`item.certificate`]="{ item }">
                <v-chip
                  :color="item.certificate_path ? 'info' : 'grey'"
                  dark
                  x-small
                >
                  <v-icon left x-small>
                    {{ item.certificate_path ? 'mdi-file-document' : 'mdi-file-document-outline' }}
                  </v-icon>
                  {{ item.certificate_path ? 'Available' : 'Not Available' }}
                </v-chip>
              </template>

              <!-- Created By Column -->
              <template #[`item.created_by`]="{ item }">
                <div v-if="item.created_by">
                  <div class="font-weight-medium">
                    {{ item.created_by.first_name + ' ' + item.created_by.last_name }}
                  </div>
                  <div class="text-caption grey--text">{{ item.created_by.email }}</div>
                </div>
                <span v-else class="grey--text">System</span>
              </template>

              <!-- Created At Column -->
              <template #[`item.created_at`]="{ item }">
                <div>
                  <div class="text-body-2">{{ formatDate(item.created_at) }}</div>
                  <div class="text-caption grey--text">{{ formatTime(item.created_at) }}</div>
                </div>
              </template>

              <!-- Actions Column -->
              <template #[`item.actions`]="{ item }">
                <div class="d-flex">
                  <v-btn
                    color="success"
                    small
                    outlined
                    class="mr-1"
                    @click="openApproveDialog(item)"
                    :loading="loadingActions[item.id]"
                  >
                    <v-icon small>mdi-check</v-icon>
                    Approve
                  </v-btn>
                  <v-btn
                    color="error"
                    small
                    outlined
                    @click="openRejectDialog(item)"
                    :loading="loadingActions[item.id]"
                  >
                    <v-icon small>mdi-close</v-icon>
                    Reject
                  </v-btn>
                </div>
              </template>
            </v-data-table>

            <!-- Custom Pagination -->
            <div class="px-4 py-2">
              <v-row align="center" justify="space-between">
                <v-col cols="12" md="6">
                  <div class="d-flex align-center">
                    <span class="text-body-2 mr-3">Rows per page:</span>
                    <v-select
                      v-model="rowsPerPage"
                      :items="[10, 25, 50, 100]"
                      dense
                      outlined
                      hide-details
                      style="max-width: 80px"
                      @change="handlePageSizeChange"
                    ></v-select>
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="d-flex justify-end">
                    <v-pagination
                      v-model="currentPage"
                      :length="totalPages"
                      :total-visible="7"
                      @input="handlePageChange"
                    ></v-pagination>
                  </div>
                </v-col>
              </v-row>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Approve Dialog -->
    <v-dialog v-model="approveDialog.show" max-width="500">
      <v-card>
        <v-card-title class="text-h6 success--text">
          <v-icon left color="success">mdi-check-circle</v-icon>
          Approve Council Certificate
        </v-card-title>
        <v-card-text>
          <div class="mb-4">
            <p>Are you sure you want to approve this council registration certificate?</p>
            <div class="d-flex align-center mt-3">
              <v-avatar size="32" class="mr-2">
                <v-icon>mdi-account</v-icon>
              </v-avatar>
              <div>
                <div class="font-weight-bold">
                  {{ approveDialog.certificate?.user ? (approveDialog.certificate.user.first_name + ' ' + approveDialog.certificate.user.last_name) : 'N/A' }}
                </div>
                <div class="text-caption grey--text">
                  {{ approveDialog.certificate?.council_name }}
                </div>
              </div>
            </div>
          </div>
          
          <v-textarea
            v-model="approveDialog.remark"
            label="Approval Remark (Optional)"
            placeholder="Add any notes about this approval..."
            outlined
            rows="3"
            counter="500"
            :rules="[v => !v || v.length <= 500 || 'Remark must be 500 characters or less']"
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeApproveDialog">Cancel</v-btn>
          <v-btn 
            color="success" 
            @click="approveCertificate"
            :loading="approveDialog.loading"
          >
            <v-icon left>mdi-check</v-icon>
            Approve
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Reject Dialog -->
    <v-dialog v-model="rejectDialog.show" max-width="500">
      <v-card>
        <v-card-title class="text-h6 error--text">
          <v-icon left color="error">mdi-close-circle</v-icon>
          Reject Council Certificate
        </v-card-title>
        <v-card-text>
          <div class="mb-4">
            <p>Please provide a reason for rejecting this council registration certificate:</p>
            <div class="d-flex align-center mt-3">
              <v-avatar size="32" class="mr-2">
                <v-icon>mdi-account</v-icon>
              </v-avatar>
              <div>
                <div class="font-weight-bold">
                  {{ rejectDialog.certificate?.user ? (rejectDialog.certificate.user.first_name + ' ' + rejectDialog.certificate.user.last_name) : 'N/A' }}
                </div>
                <div class="text-caption grey--text">
                  {{ rejectDialog.certificate?.council_name }}
                </div>
              </div>
            </div>
          </div>
          
          <v-textarea
            v-model="rejectDialog.remark"
            label="Rejection Reason *"
            placeholder="Please explain why this council certificate is being rejected..."
            outlined
            rows="4"
            counter="1000"
            :rules="rejectRemarkRules"
            :error-messages="rejectDialog.errors.rejection_reason"
            required
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeRejectDialog">Cancel</v-btn>
          <v-btn 
            color="error" 
            @click="rejectCertificate"
            :loading="rejectDialog.loading"
            :disabled="!rejectDialog.remark || rejectDialog.remark.length < 5"
          >
            <v-icon left>mdi-close</v-icon>
            Reject
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Success Snackbar -->
    <v-snackbar
      v-model="successSnackbar.show"
      :color="successSnackbar.color"
      :timeout="4000"
      top
    >
      {{ successSnackbar.message }}
      <template #action="{ attrs }">
        <v-btn text v-bind="attrs" @click="successSnackbar.show = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-main>
</template>

<script>
import axios from '../../axios'

export default {
  name: 'CouncilCertificateApproval',
  data() {
    return {
      loading: false,
      certificates: [],
      totalItems: 0,
      currentPage: 1,
      rowsPerPage: 25,
      loadingActions: {},
      
      // Filters
      filter: {
        search_keyword: '',
        sort_by: 'created_at',
      },
      
      // Header configuration
      headers: [
        { text: 'User', value: 'user_info', sortable: false, width: '250px' },
        { text: 'Council Details', value: 'council_details', sortable: false, width: '200px' },
        { text: 'Registration Year', value: 'registration_year', sortable: false, width: '150px' },
        { text: 'Certificate', value: 'certificate', sortable: false, width: '120px', align: 'center' },
        { text: 'Submitted By', value: 'created_by', sortable: false, width: '150px' },
        { text: 'Submitted On', value: 'created_at', width: '150px' },
        { text: 'Actions', value: 'actions', sortable: false, align: 'center', width: '200px' },
      ],
      
      // Sort options
      sortOptions: [
        { text: 'Oldest First', value: 'created_at' },
        { text: 'Newest First', value: 'created_at_desc' },
        { text: 'Council A-Z', value: 'council_name' },
        { text: 'Council Z-A', value: 'council_name_desc' },
      ],
      
      // Dialogs
      approveDialog: {
        show: false,
        certificate: null,
        remark: '',
        loading: false,
      },
      rejectDialog: {
        show: false,
        certificate: null,
        remark: '',
        loading: false,
        errors: {},
      },
      
      // Snackbar
      successSnackbar: {
        show: false,
        message: '',
        color: 'success',
      },
      
      // Search debounce
      searchTimeout: null,
    }
  },
  
  computed: {
    totalPages() {
      return Math.ceil(this.totalItems / this.rowsPerPage)
    },
    
    pendingCount() {
      return this.totalItems
    },
    
    hasActiveFilters() {
      return !!(this.filter.search_keyword)
    },
    
    rejectRemarkRules() {
      return [
        v => !!v || 'Rejection reason is required',
        v => (v && v.length >= 5) || 'Rejection reason must be at least 5 characters',
        v => (v && v.length <= 1000) || 'Rejection reason must be 1000 characters or less',
      ]
    },
  },
  
  watch: {
    currentPage() {
      this.fetchCertificates()
    },
  },
  
  async mounted() {
    await this.fetchCertificates()
  },
  
  methods: {
    async fetchCertificates() {
      try {
        this.loading = true
        
        const params = {
          page: this.currentPage,
          rowsPerPage: this.rowsPerPage,
          status: 0, // 0 = Pending
          ...this.filter,
        }
        
        // Handle sort parameters
        if (this.filter.sort_by.includes('_desc')) {
          params.sort_by = this.filter.sort_by.replace('_desc', '')
          params.sort_order = 'desc'
        } else {
          params.sort_by = this.filter.sort_by
          params.sort_order = 'asc'
        }
        
        const response = await axios.get('council_registration_certificates', { params })
        
        if (response.data.success) {
          this.certificates = response.data.data
          this.totalItems = response.data.count
        }
      } catch (error) {
        console.error('Error fetching pending council certificates:', error)
        this.showSnackbar('Error loading pending council certificates', 'error')
      } finally {
        this.loading = false
      }
    },
    
    // Filter methods
    handleSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1
        this.fetchCertificates()
      }, 500)
    },
    
    applyFilters() {
      this.currentPage = 1
      this.fetchCertificates()
    },
    
    clearAllFilters() {
      this.filter = {
        search_keyword: '',
        sort_by: 'created_at',
      }
      this.applyFilters()
    },
    
    getActiveFiltersCount() {
      let count = 0
      if (this.filter.search_keyword) count++
      return count
    },
    
    // Pagination methods
    handlePageChange() {
      this.fetchCertificates()
    },
    
    handlePageSizeChange() {
      this.currentPage = 1
      this.fetchCertificates()
    },
    
    // Dialog methods
    openApproveDialog(certificate) {
      this.approveDialog = {
        show: true,
        certificate: certificate,
        remark: '',
        loading: false,
      }
    },
    
    closeApproveDialog() {
      this.approveDialog = {
        show: false,
        certificate: null,
        remark: '',
        loading: false,
      }
    },
    
    openRejectDialog(certificate) {
      this.rejectDialog = {
        show: true,
        certificate: certificate,
        remark: '',
        loading: false,
        errors: {},
      }
    },
    
    closeRejectDialog() {
      this.rejectDialog = {
        show: false,
        certificate: null,
        remark: '',
        loading: false,
        errors: {},
      }
    },
    
    // Approval methods
    async approveCertificate() {
      try {
        this.approveDialog.loading = true
        this.loadingActions[this.approveDialog.certificate.id] = true
        
        const data = {
          approval_remark: this.approveDialog.remark || null,
        }
        
        const response = await axios.post(`council_registration_certificates/approve/${this.encryptIt(this.approveDialog.certificate.id)}`, data)
        
        if (response.data.success) {
          this.showSnackbar('Council certificate approved successfully', 'success')
          this.closeApproveDialog()
          await this.fetchCertificates()
        }
      } catch (error) {
        console.error('Error approving council certificate:', error)
        this.showSnackbar('Error approving council certificate', 'error')
      } finally {
        this.approveDialog.loading = false
        delete this.loadingActions[this.approveDialog.certificate?.id]
      }
    },
    
    async rejectCertificate() {
      try {
        this.rejectDialog.loading = true
        this.rejectDialog.errors = {}
        this.loadingActions[this.rejectDialog.certificate.id] = true
        
        const data = {
          rejection_reason: this.rejectDialog.remark,
        }
        
        const response = await axios.post(`council_registration_certificates/reject/${this.encryptIt(this.rejectDialog.certificate.id)}`, data)
        
        if (response.data.success) {
          this.showSnackbar('Council certificate rejected successfully', 'success')
          this.closeRejectDialog()
          await this.fetchCertificates()
        }
      } catch (error) {
        console.error('Error rejecting council certificate:', error)
        if (error.response?.data?.errors) {
          this.rejectDialog.errors = error.response.data.errors
        } else {
          this.showSnackbar('Error rejecting council certificate', 'error')
        }
      } finally {
        this.rejectDialog.loading = false
        delete this.loadingActions[this.rejectDialog.certificate?.id]
      }
    },
    
    // Utility methods
    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },
    
    formatTime(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    },
    
    showSnackbar(message, color = 'success') {
      this.successSnackbar = {
        show: true,
        message: message,
        color: color,
      }
    },
  },
}
</script>

<style scoped>
.v-data-table >>> tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.02) !important;
}
</style>